import { useEffect, useCallback } from 'react';
import { useGlobalCanvas } from '../contexts/GlobalCanvasContext';

interface UseKeyboardShortcutsOptions {
  enableGlobalShortcuts?: boolean;
  onShortcutUsed?: (shortcut: string) => void;
}

export const useKeyboardShortcuts = (options: UseKeyboardShortcutsOptions = {}) => {
  const {
    tabs,
    activeTabId,
    splitViews,
    activeSplitView,
    closeTab,
    setActiveTab,
    duplicateTab,
    popOutTab,
    closeSplitView,
    splitTabToNewView,
    createCanvas
  } = useGlobalCanvas();

  const { enableGlobalShortcuts = true, onShortcutUsed } = options;

  const handleKeyboardShortcut = useCallback((event: KeyboardEvent) => {
    const { ctrlKey, shiftKey, altKey, metaKey, key } = event;
    const modifier = ctrlKey || metaKey; // Support both Ctrl (Windows/Linux) and Cmd (Mac)

    // Don't handle shortcuts when typing in input elements
    const target = event.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.contentEditable === 'true' ||
      target.isContentEditable
    ) {
      return false;
    }

    let handled = false;
    let shortcutName = '';

    // Tab Management Shortcuts
    if (modifier && !shiftKey && !altKey && key === 't') {
      // Ctrl+T - New Canvas Tab
      event.preventDefault();
      event.stopImmediatePropagation();
      console.log('🎯 Ctrl+T pressed - creating new canvas');
      createCanvas();
      handled = true;
      shortcutName = 'New Canvas';
    } else if (modifier && !shiftKey && !altKey && key === 'w' && activeTabId) {
      // Ctrl+W - Close Active Tab
      event.preventDefault();
      event.stopImmediatePropagation();
      console.log('❌ Closing tab:', activeTabId);
      closeTab(activeTabId);
      handled = true;
      shortcutName = 'Close Tab';
    } else if (modifier && !shiftKey && !altKey && key === 'd' && activeTabId) {
      // Ctrl+D - Duplicate Active Tab
      event.preventDefault();
      duplicateTab(activeTabId);
      handled = true;
      shortcutName = 'Duplicate Tab';
    } else if (modifier && shiftKey && !altKey && key === 'T' && activeTabId) {
      // Ctrl+Shift+T - Pop Out Tab
      event.preventDefault();
      popOutTab(activeTabId);
      handled = true;
      shortcutName = 'Pop Out Tab';
    }

    // Tab Navigation Shortcuts
    else if (modifier && !shiftKey && key === 'Tab' && tabs.length > 1) {
      // Ctrl+Tab - Next Tab
      event.preventDefault();
      const currentIndex = tabs.findIndex(tab => tab.id === activeTabId);
      const nextIndex = (currentIndex + 1) % tabs.length;
      if (tabs[nextIndex]) {
        setActiveTab(tabs[nextIndex].id);
      }
      handled = true;
      shortcutName = 'Next Tab';
    } else if (modifier && shiftKey && key === 'Tab' && tabs.length > 1) {
      // Ctrl+Shift+Tab - Previous Tab
      event.preventDefault();
      const currentIndex = tabs.findIndex(tab => tab.id === activeTabId);
      const prevIndex = currentIndex - 1 < 0 ? tabs.length - 1 : currentIndex - 1;
      if (tabs[prevIndex]) {
        setActiveTab(tabs[prevIndex].id);
      }
      handled = true;
      shortcutName = 'Previous Tab';
    }

    // Direct Tab Access (Ctrl+1-9)
    else if (modifier && !shiftKey && !altKey && /^[1-9]$/.test(key)) {
      event.preventDefault();
      const tabIndex = parseInt(key) - 1;
      if (tabs[tabIndex]) {
        setActiveTab(tabs[tabIndex].id);
        handled = true;
        shortcutName = `Switch to Tab ${key}`;
      }
    }

    // Split Pane Shortcuts
    else if (modifier && !shiftKey && !altKey && key === '\\') {
      // Ctrl+\ - Vertical Split
      event.preventDefault();
      event.stopImmediatePropagation();
      if (activeTabId) {
        console.log('🔀 Splitting vertically with tab:', activeTabId);
        splitTabToNewView(activeTabId, 'vertical');
      } else {
        console.log('🔀 No active tab to split - creating new canvas first');
        createCanvas('New Canvas');
      }
      handled = true;
      shortcutName = 'Vertical Split';
    } else if (modifier && shiftKey && !altKey && (key === '|' || key === '\\')) {
      // Ctrl+Shift+\ - Horizontal Split (handles both | and \ keys)
      event.preventDefault();
      event.stopImmediatePropagation();
      if (activeTabId) {
        console.log('🔀 Splitting horizontally with tab:', activeTabId);
        splitTabToNewView(activeTabId, 'horizontal');
      } else {
        console.log('🔀 No active tab to split - creating new canvas first');
        createCanvas('New Canvas');
      }
      handled = true;
      shortcutName = 'Horizontal Split';
    } else if (modifier && !shiftKey && !altKey && key === 'Enter' && activeSplitView !== 'main') {
      // Ctrl+Enter - Close Split View
      event.preventDefault();
      event.stopImmediatePropagation();
      closeSplitView(activeSplitView);
      handled = true;
      shortcutName = 'Close Split';
    }

    // View Navigation Shortcuts
    else if (modifier && !shiftKey && altKey && key === 'ArrowLeft') {
      // Ctrl+Alt+Left - Previous Split View
      event.preventDefault();
      const currentIndex = splitViews.findIndex(view => view.id === activeSplitView);
      const prevIndex = currentIndex - 1 < 0 ? splitViews.length - 1 : currentIndex - 1;
      if (splitViews[prevIndex] && splitViews[prevIndex].activeTabId) {
        setActiveTab(splitViews[prevIndex].activeTabId!);
        handled = true;
        shortcutName = 'Previous Split View';
      }
    } else if (modifier && !shiftKey && altKey && key === 'ArrowRight') {
      // Ctrl+Alt+Right - Next Split View
      event.preventDefault();
      const currentIndex = splitViews.findIndex(view => view.id === activeSplitView);
      const nextIndex = (currentIndex + 1) % splitViews.length;
      if (splitViews[nextIndex] && splitViews[nextIndex].activeTabId) {
        setActiveTab(splitViews[nextIndex].activeTabId!);
        handled = true;
        shortcutName = 'Next Split View';
      }
    }

    // Canvas-specific Shortcuts (when canvas tab is active)
    else if (!modifier && !shiftKey && !altKey && key === 'v') {
      // V - Select tool (canvas)
      // This will be handled by the canvas component
      shortcutName = 'Select Tool';
    } else if (!modifier && !shiftKey && !altKey && key === 'h') {
      // H - Hand/Pan tool (canvas)
      shortcutName = 'Pan Tool';
    } else if (!modifier && !shiftKey && !altKey && key === 'n') {
      // N - Note tool (canvas)
      shortcutName = 'Note Tool';
    } else if (!modifier && !shiftKey && !altKey && key === 's' && !modifier) {
      // S - Shape tool (canvas)
      shortcutName = 'Shape Tool';
    } else if (!modifier && !shiftKey && !altKey && key === 'c') {
      // C - Connect tool (canvas)
      shortcutName = 'Connect Tool';
    }

    // Zoom Shortcuts
    else if (modifier && !shiftKey && !altKey && key === '=') {
      // Ctrl+= - Zoom In
      event.preventDefault();
      shortcutName = 'Zoom In';
    } else if (modifier && !shiftKey && !altKey && key === '-') {
      // Ctrl+- - Zoom Out
      event.preventDefault();
      shortcutName = 'Zoom Out';
    } else if (modifier && !shiftKey && !altKey && key === '0') {
      // Ctrl+0 - Reset Zoom
      event.preventDefault();
      shortcutName = 'Reset Zoom';
    }

    // Application Shortcuts
    else if (modifier && shiftKey && !altKey && key === 'K') {
      // Ctrl+Shift+K - Toggle Keyboard Shortcuts Help
      event.preventDefault();
      // This would show/hide a help panel
      shortcutName = 'Toggle Shortcuts Help';
    } else if (key === 'F11') {
      // F11 - Toggle Fullscreen
      event.preventDefault();
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        document.documentElement.requestFullscreen();
      }
      handled = true;
      shortcutName = 'Toggle Fullscreen';
    }

    // Escape - General cancel/deselect
    else if (!modifier && !shiftKey && !altKey && key === 'Escape') {
      // Let individual components handle this
      shortcutName = 'Escape';
    }

    // Notify callback if shortcut was used
    if (handled && onShortcutUsed && shortcutName) {
      onShortcutUsed(shortcutName);
    }

    return handled;
  }, [
    tabs,
    activeTabId,
    splitViews,
    activeSplitView,
    closeTab,
    setActiveTab,
    duplicateTab,
    popOutTab,
    closeSplitView,
    splitTabToNewView,
    createCanvas,
    onShortcutUsed
  ]);

  useEffect(() => {
    if (!enableGlobalShortcuts) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if user is typing in an input field
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const handled = handleKeyboardShortcut(event);
      if (handled) {
        event.stopPropagation();
      }
    };

    // Use capture phase to intercept before browser shortcuts
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [handleKeyboardShortcut, enableGlobalShortcuts]);

  return {
    handleKeyboardShortcut
  };
};

// Hook for showing keyboard shortcuts help
export const useKeyboardShortcutsHelp = () => {
  const shortcuts = [
    { category: 'Tab Management', items: [
      { keys: ['Ctrl', 'T'], description: 'New Canvas' },
      { keys: ['Ctrl', 'W'], description: 'Close Tab' },
      { keys: ['Ctrl', 'D'], description: 'Duplicate Tab' },
      { keys: ['Ctrl', 'Shift', 'T'], description: 'Pop Out Tab' }
    ]},
    { category: 'Tab Navigation', items: [
      { keys: ['Ctrl', 'Tab'], description: 'Next Tab' },
      { keys: ['Ctrl', 'Shift', 'Tab'], description: 'Previous Tab' },
      { keys: ['Ctrl', '1-9'], description: 'Switch to Tab by Number' }
    ]},
    { category: 'Split Panes', items: [
      { keys: ['Ctrl', '\\'], description: 'Vertical Split' },
      { keys: ['Ctrl', 'Shift', '\\'], description: 'Horizontal Split' },
      { keys: ['Ctrl', 'Enter'], description: 'Close Split' },
      { keys: ['Ctrl', 'Alt', '←/→'], description: 'Navigate Split Views' }
    ]},
    { category: 'Canvas Tools', items: [
      { keys: ['V'], description: 'Select Tool' },
      { keys: ['H'], description: 'Pan Tool' },
      { keys: ['N'], description: 'Note Tool' },
      { keys: ['S'], description: 'Shape Tool' },
      { keys: ['C'], description: 'Connect Tool' }
    ]},
    { category: 'View', items: [
      { keys: ['Ctrl', '+'], description: 'Zoom In' },
      { keys: ['Ctrl', '-'], description: 'Zoom Out' },
      { keys: ['Ctrl', '0'], description: 'Reset Zoom' },
      { keys: ['F11'], description: 'Toggle Fullscreen' }
    ]},
    { category: 'General', items: [
      { keys: ['Escape'], description: 'Cancel/Deselect' },
      { keys: ['Delete'], description: 'Delete Selected (Canvas)' },
      { keys: ['Ctrl', 'Shift', 'K'], description: 'Toggle Shortcuts Help' }
    ]}
  ];

  return { shortcuts };
};
